import React from 'react';
import { OPEN_POSITIONS_CURRENCIES, OPEN_POSITIONS_UNDERLYINGS, OPEN_POSITIONS_SORTS, formatStrategyLabel, getEffectiveUnderlyingFilter } from './optionsOpenPositionsUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { OptionsCurrencyDisplay } from './OptionsCurrencyDisplay';
import { OptionsSyncButton } from './OptionsSyncButton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const labelMap = {
  dte: 'Nearest expiration',
  pnl_desc: 'PNL +',
  pnl_asc: 'PNL −',
  net_delta: 'Net Delta',
};

const renderCurrencyPill = (item) => <OptionsCurrencyDisplay currencyKey={item} compact iconSize={15} />;

export const OptionsOpenPositionsFilters = ({
  selectedCurrency,
  onCurrencyChange,
  underlyingFilter,
  onUnderlyingChange,
  strategyFilter,
  onStrategyChange,
  sortBy,
  onSortChange,
  syncing,
  onSync,
  totalCount,
  filteredCount,
  strategyOptions = ['all'],
  showSyncButton = true,
}) => {
  const { t, formatMessage } = useOptionsLocale();
  const effectiveUnderlying = getEffectiveUnderlyingFilter({ selectedCurrency, underlyingFilter });
  const underlyingDisabled = selectedCurrency !== 'usd';
  const sortLabels = {
    dte: t('positions.sortNearestExpiry'),
    pnl_desc: t('positions.sortPnlDesc'),
    pnl_asc: t('positions.sortPnlAsc'),
    net_delta: t('positions.sortNetDelta'),
  };

  return (
    <div className="space-y-4" data-testid="options-open-positions-filters">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="space-y-2" data-testid="options-open-positions-count-block">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/40" data-typography="label" data-testid="options-open-positions-count-label">{t('positions.countLabel')}</p>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-3xl font-black text-white" data-typography="metric" data-testid="options-open-positions-count-value">{filteredCount}</p>
            <p className="text-sm text-white/55" data-testid="options-open-positions-count-helper">
              {filteredCount === totalCount
                ? formatMessage(t('positions.countAll'), { count: totalCount })
                : formatMessage(t('positions.countPartial'), { filtered: filteredCount, total: totalCount })}
            </p>
          </div>
        </div>

        {showSyncButton ? (
          <OptionsSyncButton onSync={onSync} syncing={syncing} testId="options-open-positions-sync-button" />
        ) : null}
      </div>

      <div className="grid gap-4 xl:grid-cols-[1fr,1fr,1.2fr,0.9fr]" data-testid="options-open-positions-filter-grid">
        <FilterPills
          label={t('common.view')}
          items={OPEN_POSITIONS_CURRENCIES}
          value={selectedCurrency}
          onChange={onCurrencyChange}
          formatter={renderCurrencyPill}
          testIdPrefix="options-positions-currency"
        />

        <FilterPills
          label={t('common.underlying')}
          items={OPEN_POSITIONS_UNDERLYINGS}
          value={effectiveUnderlying}
          onChange={(value) => !underlyingDisabled && onUnderlyingChange(value)}
          formatter={renderCurrencyPill}
          disabled={underlyingDisabled}
          testIdPrefix="options-positions-underlying"
        />

        <FilterPills
          label={t('common.strategy')}
          items={strategyOptions}
          value={strategyFilter}
          onChange={onStrategyChange}
          formatter={(item) => (item === 'all' ? 'ALL' : formatStrategyLabel(item))}
          compact
          testIdPrefix="options-positions-strategy"
        />

        <div className="space-y-2" data-testid="options-positions-sort-block">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40" data-typography="label" data-testid="options-positions-sort-label">{t('common.sort')}</p>
          <Select value={sortBy} onValueChange={onSortChange}>
            <SelectTrigger
              className="h-11 w-full rounded-2xl border border-white/10 bg-white/[0.03] px-4 text-sm text-white outline-none transition-colors hover:border-white/20 focus:ring-0 focus:ring-offset-0 [&>span]:text-white"
              data-testid="options-positions-sort-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-[#11161E] text-white/80">
              {OPEN_POSITIONS_SORTS.map((sortKey) => (
                <SelectItem
                  key={sortKey}
                  value={sortKey}
                  data-testid={`options-positions-sort-option-${sortKey}`}
                  className="cursor-pointer text-white/80 focus:bg-white/[0.08] focus:text-white data-[state=checked]:bg-white/[0.06] data-[state=checked]:text-white"
                >
                  {sortLabels[sortKey]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

const FilterPills = ({ label, items, value, onChange, formatter, disabled = false, compact = false, testIdPrefix }) => {
  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-block`}>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40" data-typography="label" data-testid={`${testIdPrefix}-label`}>{label}</p>
      <div className={`inline-flex w-full flex-wrap items-center gap-1.5 rounded-[18px] border border-white/10 bg-[#11161E] p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] ${disabled ? 'opacity-60' : ''}`} data-testid={`${testIdPrefix}-group`}>
        {items.map((item) => {
          const active = value === item;
          return (
            <button
              key={item}
              type="button"
              onClick={() => onChange(item)}
              disabled={disabled}
              data-testid={`${testIdPrefix}-${item.toLowerCase()}`}
              className={`inline-flex items-center rounded-[14px] px-3 py-2 text-sm font-semibold transition-all duration-200 font-body ${compact ? 'text-xs sm:text-sm' : ''} ${active ? 'bg-[#202A38] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]' : 'text-white/62 hover:bg-white/[0.04] hover:text-white'} disabled:cursor-not-allowed`}
              data-typography="nav"
            >
              {formatter(item)}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default OptionsOpenPositionsFilters;